import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import styled from 'styled-components';
import CustomTabs from '@components/dataDisplay/tab/CustomTabs';
import { allNotiRead } from '@store/firebase/notification/add-notification';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { removeAllNoti } from '@store/firebase/notification/remove-notification';
import NotiList from './NotiList';
import { CollectionPath, SettingCollectionPath } from '@typings/collections.enum';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import { Inotification } from '@typings/notification/notification.interface';
import { NOTI_TYPE } from '@typings/notification/notificationType';
import { fuego } from '@store/firebase';
import {
  notificationSettingsState,
  lastNotificationTimeState,
} from '@atom/setting/notification';
import { homeSettingState } from '@atom/setting/homeSettingState';
import { useNotificationSound } from '@hooks/useNotificationSound';
import { User } from '@typings/setting/userType.interface';
import { useNavigate } from 'react-router-dom';
import { ISoundEffect } from '@typings/setting/soundEffect.interface';
import { LOCAL_FALLBACK_URL } from '@pages/Setting/3.Homepage/Notifications/NotificationSoundPage';

const TextBtn = styled.button`
  color: var(--primaryColor);
  font-size: 12px;
  padding: 8px;
  border-radius: var(--cardBorderRadius);
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--inkColor);
  }
`;

const ControlSecContainer = styled.div`
  height: 62px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

function ControlSec() {
  const currentUserUid = useRecoilValue(currentUserIDState);

  const handleAllRead = useCallback(async () => {
    await allNotiRead({ userId: currentUserUid });
  }, [currentUserUid]);

  const handleAllDelete = useCallback(async () => {
    await removeAllNoti({ userId: currentUserUid });
  }, [currentUserUid]);

  return (
    <ControlSecContainer>
      <TextBtn
        onClick={handleAllDelete}
        style={{ color: 'var(--subTextColor)' }}
      >
        <strong>모두 삭제</strong>
      </TextBtn>
      <TextBtn onClick={handleAllRead}>
        <strong>모두 읽음</strong>
      </TextBtn>
    </ControlSecContainer>
  );
}

function Notifications() {
  const limit = 10;
  const currentUserId = fuego.auth().currentUser?.uid;
  const collectionPath = `${CollectionPath.USERS}/${currentUserId}/${CollectionPath.NOTIFICATIONS}`;
  const navigate = useNavigate();

  // 🔧 알림 설정 및 상태 관리
  const notificationSettings = useRecoilValue(notificationSettingsState);
  const lastNotificationTime = useRecoilValue(lastNotificationTimeState);
  const setLastNotificationTime = useSetRecoilState(lastNotificationTimeState);
  const homeSet = useRecoilValue(homeSettingState);
  const notifIcon = homeSet?.homepageSet?.logoImgURL || '/favicon.ico';

  // Firestore에 업로드된 알림음 구독 (앱 첫 로딩 시 preload 트리거)
  const { data: soundEffect } = useDocument<ISoundEffect>(
    SettingCollectionPath.SOUNDEFFECT,
    { listen: true },
  );
  // 업로드 파일 우선, 없으면 로컬 fallback
  const effectiveSoundUrl =
    soundEffect?.notificationSound?.url || LOCAL_FALLBACK_URL;

  // 🔧 이전 알림 상태 추적
  const [prevNotificationCount, setPrevNotificationCount] = useState(0);
  const [prevLatestNotificationId, setPrevLatestNotificationId] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // 🔧 통합 알림 훅 사용
  const { notify } = useNotificationSound({
    volume: notificationSettings.volume,
    soundUrl: effectiveSoundUrl,
    enableSound: notificationSettings.soundEnabled,
    enableBrowserNotification: notificationSettings.systemNotification,
  });

  const { data: allList, mutate: allMutate } = useCollection<Inotification>(
    collectionPath,
    {
      orderBy: ['createdAt', 'desc'],
      limit,
      listen: true,
      ignoreFirestoreDocumentSnapshotField: false,
    },
    {
      // this lets us update the local cache + paginate without interruptions
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      refreshInterval: 0,
    },
  );

  // 읽지 않은 알림 전용 구독 (브라우저 알림 송출용 최적화)
  const { data: allUnreadForNotify } = useCollection<Inotification>(
    collectionPath,
    {
      orderBy: ['createdAt', 'desc'],
      where: ['is_seen', '==', false],
      listen: true,
      limit,
      ignoreFirestoreDocumentSnapshotField: false,
    },
    {
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      refreshInterval: 0,
    },
  );

  const unreadAllList = useMemo(
    () => allUnreadForNotify || [],
    [allUnreadForNotify],
  );

  // 최신 알림의 보낸 사람 displayName 조회 (브라우저 알림용)
  const latestSenderId = allList?.[0]?.sender;
  const { data: latestSenderData } = useDocument<User>(
    latestSenderId ? `${CollectionPath.USERS}/${latestSenderId}` : null,
    { listen: true },
  );

  // 댓글 목록 (브라우저 알림 전용 소스)
  const { data: commentList, mutate: commentMutate } =
    useCollection<Inotification>(
      collectionPath,
      {
        orderBy: ['createdAt', 'desc'],
        where: ['type', '==', NOTI_TYPE.COMMENT],
        listen: true,
        limit,
        ignoreFirestoreDocumentSnapshotField: false,
      },
      {
        // this lets us update the local cache + paginate without interruptions
        revalidateOnFocus: false,
        refreshWhenHidden: false,
        refreshWhenOffline: false,
        refreshInterval: 0,
      },
    );

  // 읽지 않은 댓글 알림 전용 구독 (브라우저 알림 송출용)
  const { data: unreadCommentForNotify } = useCollection<Inotification>(
    collectionPath,
    {
      orderBy: ['createdAt', 'desc'],
      where: [
        ['type', '==', NOTI_TYPE.COMMENT],
        ['is_seen', '==', false],
      ] as any,
      listen: true,
      limit,
      ignoreFirestoreDocumentSnapshotField: false,
    },
    {
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      refreshInterval: 0,
    },
  );

  const unreadCommentList = useMemo(
    () => unreadCommentForNotify || [],
    [unreadCommentForNotify],
  );

  // 🔧 댓글 알림 전용 브라우저 알림 처리
  const [prevCommentCount, setPrevCommentCount] = useState(0);
  const [prevLatestCommentId, setPrevLatestCommentId] = useState('');

  // 🔧 알림 타입별 메시지 생성 함수
  const getNotificationMessage = useCallback(
    (notification: Inotification, senderDisplayName?: string) => {
      const { type } = notification;
      const senderName = senderDisplayName || notification.sender || '익명';

      switch (type) {
        case NOTI_TYPE.COMMENT:
          return {
            title: '💬 새 댓글',
            body: `${senderName}님이 댓글을 남겼습니다.`,
          };
        case NOTI_TYPE.GUESTBOOK:
          return {
            title: '📝 새 방명록',
            body: `${senderName}님이 방명록을 작성했습니다.`,
          };
        case NOTI_TYPE.REPLY:
          return {
            title: '💬 새 답글',
            body: `${senderName}님이 답글을 남겼습니다.`,
          };
        case NOTI_TYPE.TAGGED:
          return {
            title: '🏷️ 태그됨',
            body: `${senderName}님이 당신을 태그했습니다.`,
          };
        case NOTI_TYPE.PRESENT:
          return {
            title: '🎁 새 선물',
            body: `${senderName}님이 선물을 보냈습니다.`,
          };
        case NOTI_TYPE.CHAT:
          return {
            title: '💬 새 DM',
            body: `${senderName}님이 채팅 메시지를 보냈습니다.`,
          };
        case NOTI_TYPE.BATTLE:
          return {
            title: '⚔️ 도전장',
            body: `${senderName}님이 도전장을 보냈습니다!`,
          };
        case NOTI_TYPE.QUEST_REQUEST:
          return {
            title: '📌 퀘스트 검토 요청',
            body: notification.contents || `${senderName}님이 퀘스트 검토를 요청했습니다.`,
          };
        default:
          return {
            title: '🔔 새 알림',
            body: `${senderName}님으로부터 새 알림이 있습니다.`,
          };
      }
    },
    [],
  );

  // 🔧 새로운 알림 감지 (일반): 댓글(COMMENT)은 별도 훅에서 처리하고,
  // 그 외 알림(TAGGED, REPLY, PRESENT, GUESTBOOK 등)은 여기서 브라우저 알림 발송
  useEffect(() => {
    if (!unreadAllList || unreadAllList.length === 0) return;

    const currentNotificationCount = unreadAllList.length;
    const latestNotification = unreadAllList[0];
    const currentLatestId = latestNotification?.id || '';

    // 새로고침 후 중복 방지를 위한 last_shown_notification_id 체크
    const lastShownId =
      localStorage.getItem('last_shown_notification_id') || '';

    // 🔧 초기화 완료 후에만 알림 처리
    if (!isInitialized) {
      setPrevNotificationCount(currentNotificationCount);
      setPrevLatestNotificationId(currentLatestId);
      setIsInitialized(true);

      // 🔧 초기화 시 lastNotificationTime 설정
      if (lastNotificationTime === 0) {
        setLastNotificationTime(Date.now());
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 알림 시스템 초기화:', {
          initialCount: currentNotificationCount,
          latestId: currentLatestId.substring(0, 8) + '...',
        });
      }
      return;
    }

    // 🔧 새로운 알림 감지 조건들
    const hasNewNotifications =
      currentNotificationCount > prevNotificationCount;
    const hasNewLatestNotification =
      currentLatestId && currentLatestId !== prevLatestNotificationId;
    const shouldNotify =
      (hasNewNotifications || hasNewLatestNotification) &&
      currentLatestId !== lastShownId;

    if (shouldNotify && latestNotification) {
      // 댓글은 전용 훅에서 처리해 중복 방지
      if (latestNotification.type !== NOTI_TYPE.COMMENT) {
        // 알림 타입별 설정 체크
        const typeSettingMap: Partial<Record<string, boolean>> = {
          [NOTI_TYPE.COMMENT]: notificationSettings.commentNotification,
          [NOTI_TYPE.REPLY]: notificationSettings.replyNotification,
          [NOTI_TYPE.GUESTBOOK]: notificationSettings.guestbookNotification,
          [NOTI_TYPE.TAGGED]: notificationSettings.taggedNotification,
          [NOTI_TYPE.PRESENT]: notificationSettings.giftNotification,
          [NOTI_TYPE.CHAT]: notificationSettings.chatNotification,
          [NOTI_TYPE.BATTLE]: notificationSettings.battleNotification,
          [NOTI_TYPE.QUEST_REQUEST]: notificationSettings.questNotification,
        };
        const typeAllowed = typeSettingMap[latestNotification.type] ?? true;

        const now = Date.now();
        const timeSinceLastNotification = now - lastNotificationTime;
        if (timeSinceLastNotification > 1000) {
          if (typeAllowed) {
            const { title, body } = getNotificationMessage(
              latestNotification,
              latestSenderData?.displayName || undefined,
            );

            notify({
              title,
              body,
              tag: `notification-${latestNotification.type}-${currentLatestId}`,
              icon: notifIcon,
              badge: notifIcon,
              onClick: () => {
                if (latestNotification.targetPath) {
                  navigate(latestNotification.targetPath);
                } else if (
                  latestNotification.collectionPath &&
                  latestNotification.postId
                ) {
                  navigate(
                    `/${latestNotification.collectionPath}/${latestNotification.postId}`,
                  );
                }
              },
            });

            setLastNotificationTime(now);
          }
          // 알림이 꺼져 있어도 ID 갱신 → 설정 재활성화 시 과거 알림 재발송 방지
          try {
            localStorage.setItem('last_shown_notification_id', currentLatestId);
          } catch { }
        }
      }
    }

    // 🔧 이전 상태 업데이트
    setPrevNotificationCount(currentNotificationCount);
    setPrevLatestNotificationId(currentLatestId);
  }, [
    unreadAllList,
    isInitialized,
    prevNotificationCount,
    prevLatestNotificationId,
    lastNotificationTime,
    setLastNotificationTime,
    getNotificationMessage,
    notify,
    latestSenderData,
    navigate,
    notificationSettings.guestbookNotification,
    notificationSettings.commentNotification,
    notificationSettings.replyNotification,
    notificationSettings.taggedNotification,
    notificationSettings.giftNotification,
    notificationSettings.chatNotification,
    notificationSettings.battleNotification,
    notificationSettings.questNotification,
  ]);

  // 최신 알림의 보낸 사람 displayName 조회 (브라우저 알림용)
  useEffect(() => {
    if (!unreadCommentList || unreadCommentList.length === 0) return;

    const currentCount = unreadCommentList.length;
    const latestComment = unreadCommentList[0];
    const currentLatestId = latestComment?.id || '';

    const lastShownCommentId =
      localStorage.getItem('last_shown_comment_id') || '';

    const hasNew =
      currentCount > prevCommentCount ||
      currentLatestId !== prevLatestCommentId;

    if (hasNew && latestComment && currentLatestId !== lastShownCommentId) {
      const now = Date.now();
      const timeSinceLast = now - lastNotificationTime;
      if (timeSinceLast > 1000) {
        if (notificationSettings.commentNotification) {
          // 보낸 사람 이름 조회
          const senderId = latestComment.sender;
          // 로컬 조회 최적화: allList의 최신 발신자가 동일하면 재사용
          const reuseName =
            senderId === latestSenderId
              ? latestSenderData?.displayName
              : undefined;

          const { title, body } = getNotificationMessage(
            latestComment,
            reuseName,
          );

          notify({
            title,
            body,
            tag: `comment-${currentLatestId}`,
            icon: notifIcon,
            badge: notifIcon,
            onClick: () => {
              if (latestComment.targetPath) {
                navigate(latestComment.targetPath);
              } else if (latestComment.collectionPath && latestComment.postId) {
                navigate(
                  `/${latestComment.collectionPath}/${latestComment.postId}`,
                );
              }
            },
          });

          setLastNotificationTime(now);
        }
        // 알림이 꺼져 있어도 ID 갱신 → 설정 재활성화 시 과거 알림 재발송 방지
        try {
          localStorage.setItem('last_shown_comment_id', currentLatestId);
        } catch { }
      }
    }

    setPrevCommentCount(currentCount);
    setPrevLatestCommentId(currentLatestId);
  }, [
    unreadCommentList,
    lastNotificationTime,
    latestSenderData,
    latestSenderId,
    prevCommentCount,
    prevLatestCommentId,
    getNotificationMessage,
    notify,
    setLastNotificationTime,
    navigate,
    notificationSettings.commentNotification,
  ]);

  const { data: taggedList, mutate: taggedMutate } =
    useCollection<Inotification>(
      collectionPath,
      {
        orderBy: ['createdAt', 'desc'],
        where: ['type', '==', NOTI_TYPE.TAGGED],
        listen: true,
        limit,
        ignoreFirestoreDocumentSnapshotField: false,
      },
      {
        revalidateOnFocus: false,
        refreshWhenHidden: false,
        refreshWhenOffline: false,
        refreshInterval: 0,
      },
    );

  const tabList = [
    {
      id: '1',
      order: 0,
      value: '전체',
      contents: (
        <NotiList
          list={allList!}
          limit={limit}
          mutate={allMutate}
          collectionPath={collectionPath}
        />
      ),
    },
    {
      id: '2',
      order: 1,
      value: '댓글',
      contents: (
        <NotiList
          list={commentList!}
          limit={limit}
          mutate={commentMutate}
          collectionPath={collectionPath}
        />
      ),
    },
    {
      id: '3',
      order: 2,
      value: '태그됨',
      contents: (
        <NotiList
          list={taggedList!}
          limit={limit}
          mutate={taggedMutate}
          collectionPath={collectionPath}
        />
      ),
    },
  ];
  return (
    <>
      <CustomTabs
        tabList={tabList}
        position="flex-start"
        btnSideSec={<ControlSec />}
        customBtnId="noti"
        noUnderlineAni={true}
        handleOffset={false}
        tabBtnSecStyle={{ padding: '0 12px', height: '50px' }}
      />
    </>
  );
}
export default React.memo(Notifications);
